import { Component, HostBinding, Input, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import Vimeo from '@vimeo/player';
import { ReplaySubject, filter, takeUntil } from 'rxjs';

import { Loggable } from 'app/utils/logging/loggable';
import { notNil } from 'app/utils/stream-util';
import { EmbeddedMedia } from 'messages/websocket.messages';

import { PresentationService } from '../../services/presentation/presentation.service';
import { socialAppSelectors } from '../../state/app';

@Component({
  selector: 'app-presentation-presenter-embed',
  templateUrl: './presentation-presenter-embed.component.html',
  styleUrl: './presentation-presenter-embed.component.scss',
  standalone: false,
})
export class PresentationPresenterEmbedComponent extends Loggable implements OnDestroy {
  protected vimeoConfig!: Vimeo.Options;
  private _embeddedMedia?: EmbeddedMedia;
  protected videoTime = 0;
  protected currentVideoTime = 0;
  protected playing = false;
  protected isPresenter = false;
  @Input() @HostBinding('class.drawer-open') public isGalleryDrawerOpen = false;

  @Input() public set embeddedMedia(value: EmbeddedMedia | undefined) {
    // create vimeo player
    if (value && value.url.includes('vimeo') && !this.player) {
      const id = parseInt(value.url.split('/').pop() || '');
      this.vimeoConfig = {
        id,
        width: 640,
        loop: false,
        autoplay: value.playing,
        controls: false,
        responsive: true,
      };
      this.player = new Vimeo('vimeo-player', this.vimeoConfig);
      this.player.on('timeupdate', (data) => {
        this.currentVideoTime = data.seconds;
        this.videoTime = data.duration;
      });
      this._embeddedMedia = value;
      // sets the position of the video player to be relative to the parent container, this div is created by the vimeo player
      this.recursiveSetPositionUnset();
      // if joining late get synced
      if (value.startTime) {
        const startTime = Math.floor((Date.now() - value.startTime) / 1000);
        this.player?.setCurrentTime(startTime).catch((err) => this.error(err));
      }
      return;
    }
    // update time
    // TODO: should probably be checking startTime here too?
    if (value?.playtingTime !== undefined) {
      void this.onChangeTime(value?.playtingTime);
    }
    // update playing state
    if (value !== undefined && this._embeddedMedia?.playing !== value.playing) {
      this.onPlayMedia(value.playing);
      this.playing = value.playing;
    }

    this._embeddedMedia = value;
  }
  public get embeddedMedia(): EmbeddedMedia | undefined {
    return this._embeddedMedia;
  }
  private destroyed$ = new ReplaySubject<boolean>(1);
  private player: Vimeo | undefined;

  public constructor(
    private store: Store,
    private presentationService: PresentationService,
  ) {
    super();

    // getFeatureFlagObservable(this.store, LaunchDarklyFlags.vimeo_embed).pipe(takeUntil(this.destroyed$))
    this.store
      .select(socialAppSelectors.isUserPresentingEmbed)
      .pipe(filter(notNil), takeUntil(this.destroyed$))
      .subscribe((isUserPresenting: boolean) => {
        this.isPresenter = isUserPresenting;
      });
  }

  public ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  private recursiveSetPositionUnset(): void {
    const vimeoPlayer = document.getElementById('vimeo-player');
    const el = vimeoPlayer?.getElementsByTagName('div')[0];
    if (el) {
      el.style.cssText = 'position: unset;';
    } else {
      window.setTimeout(() => {
        this.recursiveSetPositionUnset();
      }, 500);
    }
  }

  protected requestChangeTime(time: number): void {
    void this.requestPlayMedia(true, time);
  }

  protected async onChangeTime(time: number): Promise<void> {
    const playTime: number = (await this.player?.getCurrentTime()) || 0;

    if (playTime && Math.abs(playTime - time) > 1) {
      void this.player?.setCurrentTime(time);
    }
  }

  protected async requestPlayMedia(play: boolean, playtingTime?: number): Promise<void> {
    const playTime: number = (await this.player?.getCurrentTime()) || 0;
    const newState = {
      ...this.embeddedMedia,
      playing: play,
      playtingTime: playtingTime !== undefined ? playtingTime : playTime,
    } as EmbeddedMedia;
    void this.presentationService.updatePresentationMediaPlayState(newState);
  }

  protected requestPiP() {
    if (!this.player) {
      return;
    }

    this.player.requestPictureInPicture().catch((err) => this.error(err));
  }

  protected async requestFullScreen() {
    if (!this.player) {
      return;
    }
    await this.player.requestFullscreen().catch((err) => this.error(err));
  }

  private onPlayMedia(play: boolean): void {
    if (play) {
      void this.player?.setMuted(false);
      void this.player?.setVolume(1);
      this.player?.play().catch((err) => this.error(err));
    } else {
      void this.player?.pause();
    }
  }

  protected formatedVideoTime(time: number) {
    if (!time) {
      return '';
    }

    const minutes = Math.floor(time / 60);
    const remainingSeconds = Math.floor(time) % 60;
    return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
  }
}
